import { Schema as S } from "effect";

import { defineApi } from "../client";
import {
	BacklinkList,
	CreatedNote,
	FolderCounts,
	FolderMove,
	NoteDetail,
	NoteList,
	NoteSearch,
	SavedNote,
} from "../schemas/notes";

/** `under` limits the list to that folder and every folder below it. */
export const listNotes = defineApi("ListNotes", {
	args: { limit: S.Number, under: S.optionalKey(S.String) },
	response: NoteList,
	request: ({ limit, under }) => ({
		method: "GET",
		path: "/notes",
		query: under === undefined ? { limit } : { limit, under },
	}),
});

export const searchNotes = defineApi("SearchNotes", {
	args: { q: S.String, includeRecords: S.Boolean },
	response: NoteSearch,
	request: ({ q, includeRecords }) => ({
		method: "GET",
		path: "/notes/search",
		query: { q, limit: 50, includeRecords },
	}),
});

export const createNote = defineApi("CreateNote", {
	args: { title: S.String, body: S.String, folder: S.optionalKey(S.String) },
	response: CreatedNote,
	request: (body) => ({ method: "POST", path: "/notes", body }),
});

export const getNote = defineApi("GetNote", {
	args: { id: S.String },
	response: NoteDetail,
	request: ({ id }) => ({
		method: "GET",
		path: `/notes/${encodeURIComponent(id)}`,
	}),
});

/** A stale `baseVersion` answers 409 (`ApiError.Http`, status 409). */
export const saveNote = defineApi("SaveNote", {
	args: {
		id: S.String,
		baseVersion: S.String,
		title: S.String,
		body: S.String,
	},
	response: SavedNote,
	request: ({ id, ...body }) => ({
		method: "PATCH",
		path: `/notes/${encodeURIComponent(id)}`,
		body,
	}),
});

/** The folder tree's counts, without loading every note. */
export const listFolders = defineApi("ListFolders", {
	args: {},
	response: FolderCounts,
	request: () => ({ method: "GET", path: "/notes/folders" }),
});

/** Moves one note to `folder` ("" = the root). */
export const moveNote = defineApi("MoveNote", {
	args: { id: S.String, folder: S.String },
	response: FolderMove,
	request: ({ id, folder }) => ({
		method: "POST",
		path: `/notes/${encodeURIComponent(id)}/move`,
		body: { folder },
	}),
});

/** Renames (or moves) a folder and everything below it. */
export const renameFolder = defineApi("RenameFolder", {
	args: { from: S.String, to: S.String },
	response: FolderMove,
	request: (body) => ({
		method: "POST",
		path: "/notes/folders/rename",
		body,
	}),
});

/** Notes linking to `target` (a note or record id); an unknown target answers 404. */
export const getBacklinks = defineApi("GetBacklinks", {
	args: { target: S.String },
	response: BacklinkList,
	request: ({ target }) => ({
		method: "GET",
		path: "/notes/backlinks",
		query: { target },
	}),
});
